import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { ToolSidebar } from './ToolSidebar';
import { ToolToolbar } from './ToolToolbar';
import { HorizontalStepper } from './HorizontalStepper';
import { MobileActionBar } from './MobileActionBar';
import type { ToolShellProps } from './types';

const DRAWER_STORAGE_KEY = 'hj-tool-shell-drawer';

/** Top-level app shell for every redesigned tool screen.
 *
 *  Layout rules per the designer's mobile spec:
 *  - ≥ lg (1024px): permanent 272px beige sidebar + dark workspace.
 *  - md (768-1023px): sidebar becomes an off-canvas drawer on the left,
 *    opened via a hamburger in the toolbar. Default closed; open/closed
 *    persisted in localStorage so the user's preference survives navigation.
 *  - < md (sm): same drawer pattern. (The screen-level layout — horizontal
 *    stepper instead of vertical pipeline, sticky bottom action bar — is
 *    handled inside the individual step content, not here.)
 *
 *  TopNav (AppHeader) sits above this; minHeight subtracts 68px for it.
 */
export const ToolShell: React.FC<
  ToolShellProps & {
    advanceLabel?: string;
    advanceDisabled?: boolean;
  }
> = ({
  toolName,
  toolNumber,
  toolBlurb: _toolBlurb,
  usesCredits,
  steps,
  currentStepKey,
  completedKeys,
  onStepChange,
  onBack,
  onAdvance,
  onBackToStudio,
  sessionLabel,
  sessionStatus,
  advanceLabel,
  advanceDisabled,
  showAdvance = true,
  children,
}) => {
  const stepIndex = steps.findIndex((s) => s.key === currentStepKey);
  const currentStep = steps[stepIndex];

  const [drawerOpen, setDrawerOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DRAWER_STORAGE_KEY) === 'open';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(DRAWER_STORAGE_KEY, drawerOpen ? 'open' : 'closed');
    } catch {
      /* localStorage may be unavailable (private mode, etc.) — silently ignore */
    }
  }, [drawerOpen]);

  if (!currentStep) {
    return null;
  }

  const sidebarContent = (
    <ToolSidebar
      toolName={toolName}
      toolNumber={toolNumber}
      usesCredits={usesCredits}
      steps={steps}
      currentStepKey={currentStepKey}
      completedKeys={completedKeys}
      onStepChange={(k) => {
        onStepChange(k);
        // On md/sm the drawer auto-closes after a step pick — feels expected.
        setDrawerOpen(false);
      }}
      onBackToStudio={onBackToStudio}
      sessionLabel={sessionLabel}
      sessionStatus={sessionStatus}
    />
  );

  return (
    <div
      className="lg:grid"
      style={{
        gridTemplateColumns: '272px minmax(0, 1fr)',
        borderTop: '1px solid var(--hj-hairline-soft)',
        minHeight: 'calc(100vh - 68px)',
        background: 'var(--hj-bg)',
      }}
    >
      {/* Permanent sidebar — lg and up. */}
      <div className="hidden lg:block">{sidebarContent}</div>

      {/* Drawer sidebar — below lg. Sheet renders into a portal so the
          off-canvas position is correct regardless of where ToolShell mounts. */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent
          side="left"
          className="lg:hidden p-0 w-[280px]"
          style={{
            background: 'var(--hj-beige)',
            borderRight: '1px solid var(--hj-beige-line)',
            color: 'var(--hj-beige-ink)',
          }}
        >
          {sidebarContent}
        </SheetContent>
      </Sheet>

      <section
        className="flex flex-col min-w-0"
        style={{ background: 'var(--hj-bg)' }}
      >
        <ToolToolbar
          step={currentStep}
          stepIndex={stepIndex}
          total={steps.length}
          onAdvance={onAdvance}
          onBack={onBack}
          canGoBack={stepIndex > 0}
          advanceLabel={advanceLabel}
          advanceDisabled={advanceDisabled}
          onOpenDrawer={() => setDrawerOpen(true)}
          showAdvance={showAdvance}
        />
        {/* sm-only collapsed step indicator — tap-to-expand sheet listing all
            steps. Hidden at md+ where the sidebar/drawer carries the same info. */}
        <HorizontalStepper
          steps={steps}
          currentStepKey={currentStepKey}
          completedKeys={completedKeys}
          onStepChange={onStepChange}
        />
        {/* Extra bottom padding at sm reserves room for the fixed MobileActionBar
            so the last content row isn't hidden under it. */}
        <div className="flex-1 min-w-0 px-4 sm:px-6 lg:px-7 pt-6 pb-16 md:pb-16 pb-[120px]">
          {children}
        </div>
      </section>
      {/* sm-only sticky bottom action bar — primary CTA always reachable. */}
      <MobileActionBar
        onBack={onBack}
        onAdvance={onAdvance}
        canGoBack={stepIndex > 0}
        advanceLabel={advanceLabel}
        advanceDisabled={advanceDisabled}
        showAdvance={showAdvance}
      />
    </div>
  );
};
